import type { CartUI, CartValidationErrorResponse } from '@libs/common-node';

import { CartValidationError } from '../providers/utils/error/cartValidationError';
import type { CartValidationErrorDetails, ItemsListItem } from '../types/cart';
import { CartValidationErrorKey } from './notificationMaps';

export const excludeDiscontinuedValidationErrors = (
    validationErrors: CartValidationErrorDetails[]
): CartValidationErrorDetails[] =>
    validationErrors.filter(error => error.errorCode !== CartValidationErrorKey.DISCONTINUED_PRODUCT);

export const buildItemsListFromCart = (cart: CartUI): ItemsListItem[] => {
    const itemsForParsing: ItemsListItem[] = [];

    cart.fulfillmentGroups?.forEach(group => {
        group.items.forEach(item => {
            itemsForParsing.push({
                ...item,
                storeId: `${group.storeId}`,
                fulfillmentMethod: group.fulfillmentMethod,
            });
        });
    });

    return itemsForParsing;
};

export const stripDiscontinuedFromCartValidationResponse = (
    response: CartValidationErrorResponse
): CartValidationErrorResponse | null => {
    const cartErrors = excludeDiscontinuedValidationErrors(response.errors?.cartErrors ?? []);
    const sourceLineErrors = [...(response.errors?.lineErrors ?? []), ...(response.errors?.lineItemErrors ?? [])];
    const lineErrors = sourceLineErrors
        .map(lineError => ({
            ...lineError,
            lineItemErrors: (lineError.lineItemErrors ?? []).filter(
                error => error.errorCode !== CartValidationErrorKey.DISCONTINUED_PRODUCT
            ),
        }))
        .filter(lineError => lineError.lineItemErrors.length > 0);
    const inventoryErrors = (response.inventory_errors ?? []).filter(
        error => error.errorCode !== CartValidationErrorKey.DISCONTINUED_PRODUCT
    );

    if (cartErrors.length === 0 && lineErrors.length === 0 && inventoryErrors.length === 0) {
        return null;
    }

    return {
        ...response,
        errors: {
            ...response.errors,
            cartErrors,
            lineErrors,
            lineItemErrors: [],
        },
        inventory_errors: inventoryErrors.length > 0 ? inventoryErrors : undefined,
    };
};

type ReapplyNonDiscontinuedCartValidationErrorsArgs = {
    parsedValidationErrors: CartValidationErrorDetails[];
    validationResponse: CartValidationErrorResponse;
    cartAfterRecovery?: CartUI | null;
    handleCartError: (error: unknown, itemsOverride?: ItemsListItem[]) => CartValidationErrorDetails[];
};

/**
 * After discontinued recovery clears validation state, re-applies concurrent non-discontinued
 * errors so stock and other merge failures remain visible (matches handleCartValidation behavior).
 */
export const reapplyNonDiscontinuedCartValidationErrors = ({
    parsedValidationErrors,
    validationResponse,
    cartAfterRecovery,
    handleCartError,
}: ReapplyNonDiscontinuedCartValidationErrorsArgs): void => {
    const remainingValidationErrors = excludeDiscontinuedValidationErrors(parsedValidationErrors);

    if (remainingValidationErrors.length === 0) {
        return;
    }

    const filteredResponse = stripDiscontinuedFromCartValidationResponse(validationResponse);

    if (!filteredResponse) {
        return;
    }

    const validationError = new CartValidationError(
        validationResponse.title ?? 'Cart validation failed',
        filteredResponse
    );
    const itemsForParsing = cartAfterRecovery ? buildItemsListFromCart(cartAfterRecovery) : undefined;

    handleCartError(validationError, itemsForParsing);
};
